import React, { useEffect, useState } from 'react';
import styles from '../../styles/components/sections/CTASection.module.css';
import { useHydration } from '../../utils/hooks/useHydration';
import { useSmoothScroll } from '../../utils/hooks/useSmoothScroll';

/**
 * Call-to-Action Section Component
 * Displays a prominent CTA section with background image and action buttons
 *
 * @param {Object} props
 * @param {Object} props.data - The CTA data
 * @param {String} props.data.title - CTA title
 * @param {String} props.data.description - CTA description
 * @param {String} props.data.backgroundImage - Background image URL
 * @param {Object} props.data.Button - Button object with name, url, and isHidden
 * @param {String} props.className - Optional additional CSS class
 */

const CTASection = ({ data, className = '' }) => {
    const { scrollToBooking } = useSmoothScroll();
    const [isDarkMode, setIsDarkMode] = useState(false);
    const isHydrated = useHydration();

    // Dark mode detection and management
    useEffect(() => {
        if (!isHydrated) return;

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        setIsDarkMode(mediaQuery.matches);

        const handleChange = (e) => {
            setIsDarkMode(e.matches);
        };

        mediaQuery.addEventListener('change', handleChange);

        // Check for saved theme preference
        const savedTheme = localStorage.getItem('theme');
        if (savedTheme) {
            setIsDarkMode(savedTheme === 'dark');
        }

        return () => mediaQuery.removeEventListener('change', handleChange);
    }, [isHydrated]);

    // Listen for theme changes from other components
    useEffect(() => {
        if (!isHydrated) return;

        const handleThemeChange = () => {
            const currentTheme = document.documentElement.getAttribute('data-theme');
            setIsDarkMode(currentTheme === 'dark');
        };

        // Check initial theme
        handleThemeChange();

        // Listen for theme changes
        const observer = new MutationObserver(handleThemeChange);
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });

        return () => observer.disconnect();
    }, [isHydrated]);

    if (!data) return null;

    const handleBook = () => scrollToBooking(100)

    // Helper function to create HTML from the rich text content
    const createMarkup = htmlContent => {
        return { __html: htmlContent };
    };

    return (
        <section className={`tw-container py-6 ${className}`}>
            <div className={`${styles['cta-card']} ${isDarkMode ? styles['dark-cta-card'] : ''}`}>
                <h2 className={styles['cta-heading']}>{data?.title}</h2>
                <p className={styles['cta-description']} dangerouslySetInnerHTML={createMarkup(data?.description)} />
                <div className="mt-4">
                    <button 
                        className={`btn btn-form-active ${isDarkMode ? styles['dark-cta-button'] : ''}`} 
                        onClick={handleBook}
                        style={{
                            transition: 'all 0.3s ease',
                            filter: isDarkMode ? 'brightness(1.1) contrast(1.1)' : 'none'
                        }}
                    >
                        {data?.Button?.name}
                    </button>
                </div>
            </div>
        </section>
    );
};

export default CTASection;
